import {Injectable} from '@angular/core';
import {Message, MessageType} from '@app/models/message';
import {
  ClassroomMessage,
  DataService,
  MessageRecord,
  MsgRecordType,
  RecordArgs,
  RecordStack,
} from '@app/services/data.service';
import {
  RemoteUserAction,
  RemoteUserAdditionalInfoStateModel,
  RemoteUsersInfoState
} from '@app/state/remote-users.state';
import {AccessType} from '@app/modules/shared/data/constants';
import * as RoomState from '@app/state/classroom-state.service';
import {ClassroomAction, ClassroomStateModel} from '@app/state/classroom-state.service';
import {Observable} from 'rxjs';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngxs/store';
import {MessageHandler} from '@app/services/message-handler';
import {SharedHandlersService} from '@app/modules/shared/services/shared-handlers.service';
import {ChatCommands, ChatEvents, ChatStates} from '@app/models/commands/chat-commands';
import {ModuleService} from '@app/services/module-service';
import {map, tap} from 'rxjs/operators';
import {UtilService} from '@app/modules/shared/services/util.service';

@Injectable({
  providedIn: 'root'
})
export class ChatService implements MessageHandler, ModuleService {

  private dataService: DataService;
  private classroom: ClassroomStateModel;
  private localUser: LocalUser;
  private remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel } = {};

  public handleRecordStack: (recordStack: RecordStack) => Promise<void>; /* should be set in ChatCommands */

  constructor(
    private sharedHandlerService: SharedHandlersService,
    private http: HttpClient,
    private store: Store
  ) {
    this.listenStoreChanges();
  }

  public setDataService(dataService: DataService): void {
    this.dataService = dataService;
  }

  public listenStoreChanges() {
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
        this.localUser = localUser;
      }
    );
    this.store.select(RoomState.ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
    this.store.select(RemoteUsersInfoState.getUsersInfo).subscribe(usersInfoById => {
      this.remoteUsers = usersInfoById;
    });
  }

  public init(): void {
    /* clear chat */
    this.store.dispatch(new RemoteUserAction.ClearAllMessages());

    this.getState().subscribe();
  }

  public getState(): Observable<any> {
    Object.keys(this.remoteUsers).forEach((idStr: string) => {
      const id: number = parseInt(idStr);
      this.store.dispatch(new RemoteUserAction.ResetRemoteUserScoreCounter(id));
    });
    this.store.dispatch(new RemoteUserAction.ResetAllScoreCounters());
    return this.http.get(`${this.classroom.vcrURL}/chat/get-state`)
      .pipe(map((result: any) => {
        console.log(`GET STATE - ${result.message}`);
        this.handleRecordStack(result.data as RecordStack).then();
      }));
  }

  public sendMessage(messageModel: Message): Observable<any> {
    const data: string = JSON.stringify({messageModel});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        ChatEvents.MESSAGE,
        new RecordArgs(data, messageModel.targetId)
      )
    };
    let endpoint = 't';
    if (messageModel.messageType === MessageType.TO_STUDENT) {
      endpoint = 'tp';
    } else if (messageModel.messageType === MessageType.TO_STAFF) {
      endpoint = 'sp';
    }
    return this.send(`${this.classroom.vcrURL}/chat/${endpoint}`, message);
  }
  public onMessage(record: MessageRecord): void {
    const recordData: any = JSON.parse(record.data);
    const messageModel: Message = recordData.messageModel;
    const targetUser: LocalUser | RemoteUserAdditionalInfoStateModel =
      this.localUser.id === messageModel.targetId ?
        this.localUser :
        this.remoteUsers[messageModel.targetId];

    const sender: RemoteUserAdditionalInfoStateModel | LocalUser = this.localUser.id === messageModel.senderId ?
      this.localUser :
      this.remoteUsers[messageModel.senderId];

    switch (messageModel.messageType) {
      case MessageType.TO_STUDENT: {
        /* if message is not sent to local student */
        if (this.localUser.id !== messageModel.targetId) {
          this.store.dispatch(new RemoteUserAction.AddMessageToRemoteUser(messageModel.targetId, messageModel));
        }
        break;
      }
      case MessageType.TO_STAFF: {
        /* if sender is local user */
        if (this.localUser.id === messageModel.senderId) {
          /* if message is not sent to local user, teacher sent message to support and receiver is teacher */
          if (this.localUser.id !== messageModel.targetId && messageModel.targetId !== -1) {
            this.store.dispatch(new RemoteUserAction.AddMessageToRemoteUser(messageModel.targetId, messageModel));
            if (targetUser.accessType === AccessType.TEACHER) {
              this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(messageModel.targetId, false));
            } else {
              this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(messageModel.targetId, true));
            }
          } else {
            this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(true));
          }
        } else { /* if sender is not local user */
          if (messageModel.targetId === -1)  { /* sent from student */
            this.store.dispatch(new RemoteUserAction.AddMessageToRemoteUser(messageModel.senderId, messageModel));
            this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(messageModel.senderId, true));
          } else if (this.localUser.id !== messageModel.targetId) { /* sent from staff to not local staff user */
            this.store.dispatch(new RemoteUserAction.AddMessageToRemoteUser(messageModel.targetId, messageModel));
            this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(messageModel.targetId, true));
          } else { /* sent from staff to local staff user */
            // /* sender can't be the local user */
            // const sender: RemoteUserAdditionalInfoStateModel = this.remoteUsers[messageModel.senderId];

            if (
              targetUser.accessType !== AccessType.STUDENT &&
              sender.accessType !== AccessType.STUDENT
            ) {
              if (this.localUser.accessType !== AccessType.TEACHER) {
                this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(true));
              }
            } else {
              this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(false));
              this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(messageModel.senderId, true));
            }
          }
        }
        break;
      }
      case MessageType.SEEN: {
        if (this.localUser.id === messageModel.targetId) {
          this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(false));
        } else {
          this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(messageModel.targetId, false));
        }
      }
    }

    if (targetUser && sender.accessType === AccessType.TEACHER) {
      if (targetUser.id === this.localUser.id) {
        this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(false));
      } else {
        this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(messageModel.targetId, false));
      }
    }

    if (messageModel.messageType !== MessageType.SEEN) {
      if (
        this.localUser.accessType !== AccessType.STUDENT ||
        messageModel.messageType === MessageType.TO_ALL ||
        (messageModel.messageType === MessageType.TO_STUDENT && messageModel.targetId === this.localUser.id) ||
        (messageModel.messageType === MessageType.TO_STAFF && messageModel.senderId === this.localUser.id)
      ) {
        this.store.dispatch(new ClassroomAction.AddMessage(messageModel));
      }
    }
  }

  public sendClassWideMessage(messageModel: Message): Observable<any> {
    const data: string = JSON.stringify({messageModel});
    const message: ClassroomMessage = {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        ChatEvents.CLASS_WIDE_MESSAGE,
        new RecordArgs(data, messageModel.targetId)
      )
    };
    const endpoint = 'class-wide';
    return this.send(`${this.classroom.vcrURL}/chat/${endpoint}`, message);
  }

  public onClassWideMessage(record: MessageRecord): void{
    const recordData: any = JSON.parse(record.data);
    const messageModel: Message = recordData.messageModel;
    switch (messageModel.messageType) {
      case MessageType.TO_STUDENT: {
        /* if message is not sent to local student */
        if (this.localUser.id !== messageModel.targetId) {
          this.store.dispatch(new RemoteUserAction.AddClassWideMessageToRemoteUser(messageModel.targetId, messageModel));
        }
        break;
      }
      case MessageType.TO_STAFF: {
        /* if sender is local user */
        if (this.localUser.id === messageModel.senderId) {
          /* if message is not sent to local user, teacher sent message to support and receiver is teacher */
          if (this.localUser.id !== messageModel.targetId && messageModel.targetId !== -1) {
            this.store.dispatch(new RemoteUserAction.AddClassWideMessageToRemoteUser(messageModel.targetId, messageModel));
          }
        } else { /* if sender is not local user */
          if (messageModel.targetId === -1)  { /* sent from student */
            this.store.dispatch(new RemoteUserAction.AddClassWideMessageToRemoteUser(messageModel.senderId, messageModel));
          } else if (this.localUser.id !== messageModel.targetId) { /* sent from staff to not local staff user */
            this.store.dispatch(new RemoteUserAction.AddClassWideMessageToRemoteUser(messageModel.targetId, messageModel));
          } else { /* sent from staff to local staff user */
            const targetUser: LocalUser | RemoteUserAdditionalInfoStateModel =
              this.localUser.id === messageModel.targetId ?
                this.localUser :
                this.remoteUsers[messageModel.targetId];

            /* sender can't be the local user */
            const sender: RemoteUserAdditionalInfoStateModel = this.remoteUsers[messageModel.senderId];
          }
        }
        break;
      }
    }
    if (messageModel.messageType !== MessageType.SEEN) {
      if (
        this.localUser.accessType !== AccessType.STUDENT ||
        messageModel.messageType === MessageType.TO_ALL ||
        (messageModel.messageType === MessageType.TO_STUDENT && messageModel.targetId === this.localUser.id) ||
        (messageModel.messageType === MessageType.TO_STAFF && messageModel.senderId === this.localUser.id)
      ) {
        this.store.dispatch(new ClassroomAction.AddClassWideMessage(messageModel));
      }
    }
  }

  public sendClearMessages(): Observable<any> {
    const eventNames = JSON.stringify([ChatEvents.MESSAGE]);
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        ChatEvents.CLEAR_MESSAGES,
        new RecordArgs(eventNames)
      )
    };
    return this.send(`${this.classroom.vcrURL}/chat/clearEvents`, message);
  }
  public onClearMessages(): void {
    this.store.dispatch(new RemoteUserAction.ClearAllMessages());
    this.store.dispatch(new ClassroomAction.ClearAllMessages());
    this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(false));
  }

  public sendClearUserMessages(targetId: number): Observable<any> {
    const eventNames = JSON.stringify([ChatEvents.MESSAGE]);
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        ChatEvents.CLEAR_USER_MESSAGES,
        new RecordArgs(eventNames, targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/chat/clearEvents`, message);
  }
  public onClearUserMessages(record: MessageRecord): void {
    if (this.localUser.id !== record.targetId) {
      this.store.dispatch(new RemoteUserAction.ClearUserMessages(record.targetId));
      this.store.dispatch(new RemoteUserAction.SetHasUnansweredMessage(record.targetId, false));
    }

    if (this.localUser.id === record.targetId) {
      this.store.dispatch(new LocalUserAction.SetHasUnansweredMessage(false));
    }
    this.store.dispatch(new ClassroomAction.ClearUserMessages(record.targetId, false));
  }

  public sendResetScore(targetId: number): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        ChatEvents.RESET_SCORE,
        new RecordArgs(ChatStates.SCORE, targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/chat/resetScore`, message);
  }
  public onResetScore(record: MessageRecord): void {
    if (record.targetId > 0) {
      this.store.dispatch(new RemoteUserAction.ResetRemoteUserScoreCounter(record.targetId));
    } else {
      this.store.dispatch(new RemoteUserAction.ResetAllScoreCounters());
    }
  }

  public sendScore(targetId: number, increment: boolean): Observable<any> {
    const message: ClassroomMessage = {
      store: false,
      broadcast: true,
      record: this.buildRecord(
        ChatStates.SCORE,
        new RecordArgs(JSON.stringify({}), targetId)
      )
    };
    return this.send(`${this.classroom.vcrURL}/chat/tp-score?increment=${increment}`, message)
      .pipe(tap((result: any) => {
        if (result && result.data) {
          this.onScore(result.data as MessageRecord);
        }
      }));
  }

  public onScore(record: MessageRecord): void {
    const scoreMap: {[key: number]: number} = JSON.parse(record.data);
    // eslint-disable-next-line guard-for-in
    Object.keys(scoreMap).forEach((userId: string) => {
      if (parseInt(userId) === this.localUser.id) {
        this.store.dispatch(new LocalUserAction.SetCorrectAnswer(scoreMap[userId]));
      } else {
        this.store.dispatch(new RemoteUserAction.SetCorrectAnswer(parseInt(userId), scoreMap[userId]));
      }
    });
  }

  public sendSystemInfoMessage(user: LocalUser,systemMessage: string): void{
    const messageModel = new Message();
    messageModel.messageType = MessageType.SYSTEM;
    messageModel.text = `${UtilService.generateFullName(user)} ` + systemMessage;
    messageModel.senderId = user.id;
    this.sendClassWideMessage(messageModel).subscribe();
  }

  public buildRecord(command: string, args: RecordArgs): MessageRecord {
    const msgType = ChatStates[command] ? MsgRecordType.STATE : ChatEvents[command] ? MsgRecordType.EVENT : null;
    return {
      category: ChatCommands.category,
      name: command,
      data: args.data,
      type: msgType,
      ownerId: null, /* must be set server-side on receive */
      targetId: args.targetId
    };
  }

  public onRemindMessages(record: MessageRecord): void {
   // const message= record;
  }

  private send(destination: string, data: object, options?: object): Observable<object> {
    return this.http.post(destination, data, options);
  }
}
